/**
 * BoudreauDiagenesisPage — Boudreau 1997 1-D pore-water diagenesis
 *
 * Steady-state O₂ profile: O₂(z) = O₂(0)·exp(-z/L_O2),  L_O2 = √(D_eff/k)
 * Reports aerobic depth, denitrification onset, Fe(III) reduction depth.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Mountain, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

export default function BoudreauDiagenesisPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('boudreau_diagenesis');
  const [DO_water, setDO] = useState(6.0);        // mg/L bottom-water DO
  const [porosity, setPorosity] = useState(0.78);
  const [tortuosity, setTort] = useState(1.4);    // sediment tortuosity
  const [k_resp, setK_resp] = useState(0.5);       // d^-1 first-order O2 consumption
  const [T, setT] = useState(12);                  // sediment T (°C)

  const R = useMemo(() => {
    // Free-water O2 diffusivity (Stokes-Einstein, T in K)
    const D0 = 1.97e-9 * (T + 273.15) / 293.15;     // m²/s
    // Effective diffusivity in sediment (Berner 1980)
    const D_eff = D0 * porosity / tortuosity;
    // Consumption rate constant in s^-1
    const k = k_resp / 86400;
    // Diffusion length scale (mm)
    const L_O2_m = Math.sqrt(D_eff / Math.max(1e-12, k));
    const L_O2_mm = L_O2_m * 1000;
    // Aerobic depth where O2 drops to 1% (Cai & Sayles 1996)
    const aerobic_mm = L_O2_mm * Math.log(100);
    // Denitrification onset ≈ 3× aerobic depth (Boudreau 1997 typical)
    const denitrif_mm = aerobic_mm * 3;
    // Fe(III) reduction onset ≈ 8× aerobic depth
    const fe_mm = aerobic_mm * 8;
    // Profile
    const profile = Array.from({ length: 20 }, (_, i) => {
      const z_mm = (i / 19) * Math.max(20, aerobic_mm * 1.5);
      const z_m = z_mm / 1000;
      return { z_mm, DO: DO_water * Math.exp(-z_m / L_O2_m) };
    });
    return { D0, D_eff, L_O2_m, L_O2_mm, aerobic_mm, denitrif_mm, fe_mm, profile };
  }, [DO_water, porosity, tortuosity, k_resp, T]);

  const fmt = (n: number, d = 2) => isFinite(n) ? n.toFixed(d) : '—';

  const svgW = 720, svgH = 280;
  const padL = 50, padR = 30, padT = 20, padB = 30;
  const plotW = svgW - padL - padR, plotH = svgH - padT - padB;
  const xMax = DO_water * 1.05;
  const yMax = Math.max(...R.profile.map(p => p.z_mm));

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Mountain} title="Module — Boudreau Pore-Water Diagenesis" subtitle="1-D steady-state sediment O₂ profile + redox depths"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('boudreau_diagenesis')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="boudreau_diagenesis" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border-2 border-emerald-700 bg-emerald-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-emerald-300">Aerobic depth</div>
          <div className="text-2xl font-bold text-emerald-200 mt-1">{fmt(R.aerobic_mm, 2)} mm</div>
          <div className="text-xs text-slate-500 mt-1">to 1% DO</div>
        </div>
        <div className="rounded-xl border border-amber-700 bg-amber-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-amber-300">Denitrification onset</div>
          <div className="text-2xl font-bold text-amber-200 mt-1">{fmt(R.denitrif_mm, 1)} mm</div>
        </div>
        <div className="rounded-xl border border-red-700 bg-red-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-red-300">Fe(III) reduction</div>
          <div className="text-2xl font-bold text-red-200 mt-1">{fmt(R.fe_mm, 1)} mm</div>
          <div className="text-xs text-slate-500 mt-1">P release zone below</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">D_eff (sediment)</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{R.D_eff.toExponential(2)}</div>
          <div className="text-xs text-slate-500 mt-1">m²/s · ϕ {porosity}, τ {tortuosity}</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-5 gap-3 text-xs">
        {[
          { l: 'Bottom-water DO (mg/L)',     v: DO_water, set: setDO, min: 0, max: 14, step: 0.1 },
          { l: 'Sediment T (°C)',             v: T,        set: setT,  min: 2,  max: 28, step: 0.5 },
          { l: 'Porosity ϕ',                  v: porosity, set: setPorosity, min: 0.3, max: 0.95, step: 0.01 },
          { l: 'Tortuosity τ',                v: tortuosity, set: setTort, min: 1, max: 3, step: 0.05 },
          { l: 'O₂ consumption k (d⁻¹)',      v: k_resp,   set: setK_resp, min: 0.1, max: 3, step: 0.05 },
        ].map(f => (
          <div key={f.l}>
            <label className="text-slate-400 block mb-1">{f.l}: <strong className="text-slate-100">{fmt(f.v, 2)}</strong></label>
            <input type="range" min={f.min} max={f.max} step={f.step} value={f.v}
              onChange={e => f.set(Number(e.target.value))} className="w-full accent-sky-500" />
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Pore-water DO profile</div>
        <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto bg-slate-950 rounded">
          <line x1={padL} y1={padT} x2={padL} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
          <line x1={padL} y1={padT + plotH} x2={padL + plotW} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
          <text x={padL + plotW / 2} y={padT + plotH + 20} textAnchor="middle" fontSize={10} fill="#94a3b8">DO (mg/L)</text>
          <text x={20} y={padT + plotH / 2} textAnchor="middle" fontSize={10} fill="#94a3b8" transform={`rotate(-90, 20, ${padT + plotH / 2})`}>Depth (mm)</text>
          {/* Aerobic zone shading */}
          <rect x={padL} y={padT} width={plotW} height={(R.aerobic_mm / yMax) * plotH}
            fill="rgba(34, 197, 94, 0.10)" />
          {/* Profile */}
          <polyline fill="none" stroke="#38bdf8" strokeWidth={2}
            points={R.profile.map(p => `${padL + (p.DO / xMax) * plotW},${padT + (p.z_mm / yMax) * plotH}`).join(' ')} />
          {/* Zone labels */}
          <text x={padL + plotW - 10} y={padT + (R.aerobic_mm / yMax) * plotH - 4} textAnchor="end" fontSize={9} fill="#22c55e">aerobic</text>
          <text x={padL + plotW - 10} y={padT + (R.aerobic_mm / yMax) * plotH + 12} textAnchor="end" fontSize={9} fill="#f59e0b">denitrification ({fmt(R.denitrif_mm, 1)} mm)</text>
        </svg>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> What this module does</div>
        <p>Boudreau 1997 reduced the full reactive-transport equations to a 1-D steady-state form when bioturbation
        is weak. The aerobic depth controls whether Fe(III) stays oxidised (P stays bound) or whether the bed
        flips anoxic and starts releasing phosphate. The denitrification onset depth controls NO₃ losses to N₂.
        Fe(III) reduction onset marks the depth below which dissolved Fe²⁺ and P escape to the water column.</p>
        <p className="mt-2">When enabled, this module exposes the depths to the Validation Steps page as part of L6 Sediment.
        It complements the Resuspension Penalty module (Bryant 2011) which addresses when plume shear breaches
        this aerobic crust.</p>
        <p className="italic text-slate-500 mt-2">References: Boudreau 1997 (Diagenetic Models); Berner 1980; Rasmussen & Jørgensen 1992; Cai & Sayles 1996.</p>
      </div>
    </div>
  );
}
